import {
  AppBar,
  Box,
  Toolbar,
  Typography,
  Button,
  MenuItem,
  Link,
  Avatar,
  Tooltip,
  Menu,
  IconButton,
  Autocomplete,
  TextField,
  Theme,
  Stack
} from "@mui/material";
import { lighten } from "@mui/material/styles";

import React, { useEffect, useState } from "react";
import { Link as RouterLink, Route, useHistory } from "react-router-dom";
import { getAdminRoutes, AppRoute, DirectLink, NavCategory, RouteConfig } from "Routes";
import { useMsal } from "@azure/msal-react";
import { useCurrentUser, User, Permissions, UsersWhoseInasMayBeViewedByOthersQuery } from "users";
import { useAppConfig } from "util/AppConfig";
import { ResetTestDataDialog } from "test-infra/ResetTestDataDialog";
import NavSubMenu from "./NavSubMenu";
import GoToPrField from "practice-reviews/GoToPrField";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCaretDown } from "@fortawesome/free-solid-svg-icons/faCaretDown";
import { makeStyles } from "../makeStyles";
import { useQuery } from "@apollo/client";
import { useInasUser } from "../InasUserProvider";
import _ from "lodash";
import { CustomReport, FetchCustomReportsQuery } from "../custom-reports";
import { versionIndicatorStyles } from "styles/common";

export function getMainBarHeight(theme: Theme) {
  return theme.spacing(12);
}
export function getSubBarHeight(theme: Theme) {
  return theme.spacing(7.5);
}
export function getFullAppBarHeight(theme: Theme) {
  return theme.spacing(19.5);
}

const useStyles = makeStyles()((theme) => {
  const mainBarHeight = getMainBarHeight(theme);
  return {
    appBar: {
      transition: "unset",
      boxShadow: "unset"
    },
    mainBar: {
      height: mainBarHeight,
      backgroundColor: theme.palette.common.white,
      borderWidth: "6px 0 0 0",
      borderStyle: "solid",
      borderImageSlice: "1",
      borderImageSource: `linear-gradient(to right, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`
    },
    grow: {
      flexGrow: 1
    },
    logoBg: {
      padding: theme.spacing(0.5, 0.5, 0.5, 0),
      margin: theme.spacing(0, 1.5, 0, 0),
      height: mainBarHeight,
      backgroundColor: "transparent",
      "& img": {
        maxWidth: "100%",
        height: "100%"
      }
    },
    homeLink: {
      flexShrink: 0,
      display: "flex",
      alignItems: "center",
      color: "inherit",
      textDecoration: "inherit"
    },
    title: {
      color: theme.palette.text.primary,
      fontWeight: 700,
      marginRight: theme.spacing(5)
    },
    versionIndicator: {
      ...versionIndicatorStyles(theme).versionIndicator,
      color: theme.palette.text.secondary,
      marginRight: theme.spacing(5),
      marginBottom: "-15px"
    },
    tabs: {
      display: "flex",
      height: "100%"
    },
    tab: {
      display: "flex",
      alignItems: "center",
      justifyItems: "center",
      paddingLeft: theme.spacing(5),
      paddingRight: theme.spacing(5),
      color: theme.palette.primary.main,
      fontSize: theme.typography.h4.fontSize,
      fontFamily: theme.typography.fontFamily,
      fontWeight: 500,
      cursor: "pointer",
      "&.selected": {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.common.white
      },
      "&:hover:not(.selected)": {
        backgroundColor: lighten(theme.palette.primary.main, 0.9)
      }
    },
    tabPanel: {
      height: getSubBarHeight(theme),
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.common.white,
      paddingLeft: theme.spacing(3),
      paddingRight: theme.spacing(3)
    },
    navButton: {
      color: theme.palette.common.white,
      borderRadius: 0,
      paddingLeft: theme.spacing(3),
      paddingRight: theme.spacing(3),
      fontFamily: theme.typography.fontFamily,
      "&:hover": {
        color: theme.palette.common.white,
        backgroundColor: theme.palette.primary.dark
      }
    },
    mainButton: {
      // this is to style them like <Button> is but let them be <a> tags
      textTransform: "uppercase",
      lineHeight: 1.75,
      minWidth: "64px",
      whiteSpace: "normal",
      textAlign: "center",
      fontSize: "0.9375rem"
    },
    userInfo: {
      fontWeight: 500
    },
    goToPrField: {
      marginLeft: "auto",
      marginRight: theme.spacing(3)
    },
    initialsAvatar: {
      color: theme.palette.getContrastText(theme.palette.secondary.main),
      backgroundColor: theme.palette.secondary.main
    },
    viewOtherUsersInasSelector: {
      width: "20em",
      "& .MuiOutlinedInput-root": {
        backgroundColor: theme.palette.common.white
      }
    },
    reportMenuButton: {
      textTransform: "unset"
    }
  };
});

enum Tabs {
  Main,
  AdminTasks,
  Reports
}

interface SiteNavItemProps {
  title: string;
  path?: AppRoute | string;
  isHref?: true;
  condition?: any;
  className?: string;
}

const SiteNavItem: React.FunctionComponent<SiteNavItemProps> = (props) => {
  const { classes } = useStyles();
  return (
    <MenuItem
      key={props.title}
      className={`${classes.navButton} ${props.className}`}
      component={props.isHref ? Link : RouterLink}
      {...{
        to: props.isHref ? undefined : props.path,
        href: props.isHref ? props.path : undefined,
        target: props.isHref ? "_blank" : undefined
      }}>
      {props.title}
    </MenuItem>
  );
};

interface SiteNavRouteProps {
  route: RouteConfig | DirectLink;
  userHasPermission: (permission: Permissions) => boolean;
  nocheck?: boolean;
  className?: string;
}

const SiteNavRoute: React.FunctionComponent<SiteNavRouteProps> = (props) => {
  const { classes } = useStyles();
  if (
    "authorizedPermissions" in props.route &&
    props.route.authorizedPermissions?.length &&
    !props.nocheck &&
    !props.route.authorizedPermissions.some((perm) => props.userHasPermission(perm))
  ) {
    return null;
  }

  return (
    <MenuItem
      key={props.route.navLabel}
      className={`${classes.navButton} ${props.className}`}
      component={"href" in props.route ? Link : RouterLink}
      {...{
        to: "path" in props.route ? props.route.path : undefined,
        href: "href" in props.route ? props.route.href : undefined,
        target: "href" in props.route ? "_blank" : undefined
      }}>
      {props.route.navLabel}
    </MenuItem>
  );
};

interface SiteNavCategoryProps {
  navCategory: NavCategory;
  userHasPermission: (permission: Permissions) => boolean;
}
const SiteNavCategory: React.FunctionComponent<SiteNavCategoryProps> = (props) => {
  const { classes } = useStyles();
  const [open, setOpen] = useState<boolean>(false);
  const buttonRef = React.useRef<any>();

  const visibleNavItems = props.navCategory.navItems?.filter((route) => {
    // Keep items that don't require permissions, or user has at least one required permission
    return (
      "authorizedPermissions" in route &&
      (!route.authorizedPermissions ||
        route.authorizedPermissions.length === 0 ||
        route.authorizedPermissions.some((perm: Permissions) => props.userHasPermission(perm)))
    );
  });

  if (!visibleNavItems?.length) return null;

  return (
    <>
      <Button className={classes.navButton} size="large" onClick={() => setOpen(true)} ref={buttonRef}>
        {props.navCategory.categoryName}
        <Box sx={{ ml: 1 }}>
          <FontAwesomeIcon icon={faCaretDown} />
        </Box>
      </Button>
      <NavSubMenu anchor={buttonRef} open={open} onClose={() => setOpen(false)}>
        {visibleNavItems?.map((item) => (
          <SiteNavRoute key={item.navLabel} route={item} userHasPermission={props.userHasPermission} nocheck />
        ))}
      </NavSubMenu>
    </>
  );
};

interface TopLevelTabBodyProps {
  tab: Tabs;
  currentTab: Tabs;
  userHasPermission: (permission: Permissions) => boolean;
  items: (NavCategory | RouteConfig)[];
}

const TopLevelTabBody: React.FunctionComponent<TopLevelTabBodyProps> = (props) => {
  const { classes } = useStyles();
  if (props.tab !== props.currentTab) return null;
  return (
    <Stack direction="row" spacing={1} alignItems="center" className={classes.tabPanel} flexWrap="nowrap">
      {props.items.map((item) => {
        const asCategory = item as NavCategory;
        const asRoute = item as RouteConfig;
        if (asCategory.categoryName) {
          return <SiteNavCategory key={asCategory.categoryName} navCategory={asCategory} userHasPermission={props.userHasPermission} />;
        }
        return (
          <SiteNavRoute key={asRoute.path} route={asRoute} userHasPermission={props.userHasPermission} className={classes.mainButton} />
        );
      })}
    </Stack>
  );
};

const SiteAppBar: React.FunctionComponent = () => {
  const { classes, cx } = useStyles();
  const history = useHistory();
  const { instance: msalInstance } = useMsal();
  const { user, userLoading, userHasPermission } = useCurrentUser();
  const appConfig = useAppConfig();
  const adminRoutes = getAdminRoutes(appConfig);
  const [resetDataDialogIsVisible, setResetDataDialogIsVisible] = useState(false);

  const [tabIndex, setTabIndex] = useState<Tabs>(Tabs.Main);
  const [route, setRoute] = useState(history.location.pathname);

  const [reportsMenuOpen, setReportsMenuOpen] = useState<boolean>(false);
  const reportsMenuButton = React.useRef<any>();

  const [userMenuAnchorElement, setUserMenuAnchorElement] = useState<HTMLElement | null>(null);

  const { inasUser, setInasUser } = useInasUser();

  const userQuery = useQuery<{ usersWhoseInasMayBeViewedByOthers: User[] }>(UsersWhoseInasMayBeViewedByOthersQuery);
  const usersWhoseInasCanBeViewed = userQuery.data?.usersWhoseInasMayBeViewedByOthers ?? [];

  const customReportsQuery = useQuery<{ customReports: CustomReport[] }>(FetchCustomReportsQuery);
  const customReports = customReportsQuery.data?.customReports || [];

  useEffect(() => {
    const deregisterListener = history.listen(() => {
      setRoute(history.location.pathname);
    });

    return () => {
      deregisterListener();
    };
  }, [history]);

  interface TopLevelTabHeaderProps {
    tabIndex: Tabs;
    displayText: string;
    items: (NavCategory | RouteConfig)[];
  }

  const TopLevelTabHeader: React.FunctionComponent<TopLevelTabHeaderProps> = (props) => {
    // Flatten children to collect all RouteConfig objects
    const routeConfigs: (RouteConfig | DirectLink)[] = [];
    props.items.forEach((child) => {
      if ((child as NavCategory).navItems) {
        // It's a NavCategory
        const navItems = (child as NavCategory).navItems;
        if (Array.isArray(navItems)) {
          routeConfigs.push(...navItems);
        }
      } else if ((child as RouteConfig).path) {
        // It's a RouteConfig
        routeConfigs.push(child as RouteConfig);
      } else if ((child as DirectLink).href) {
        // It's a DirectLink
        routeConfigs.push(child as DirectLink);
      }
    });

    // Check if any RouteConfig is visible to the user
    const hasVisibleRoute = routeConfigs.some((route) => {
      if (!("authorizedPermissions" in route) || !route.authorizedPermissions || route.authorizedPermissions.length === 0) {
        return true;
      }
      return route.authorizedPermissions.some((perm: Permissions) => userHasPermission(perm));
    });

    if (!hasVisibleRoute) return null;

    return (
      <div className={cx(classes.tab, { selected: tabIndex === props.tabIndex })} onClick={() => setTabIndex(props.tabIndex)}>
        {props.displayText}
      </div>
    );
  };

  return (
    <AppBar position="sticky" color="default" className={classes.appBar}>
      <Toolbar className={classes.mainBar}>
        <RouterLink to="/" className={classes.homeLink}>
          <Box className={classes.logoBg} title={route !== AppRoute.INAs ? "Return to My INAs" : undefined}>
            <img src="/CPA_AB_4c_pro_condensed.svg" alt="CPAA logo" />
          </Box>
        </RouterLink>
        <div style={{ textAlign: "right" }}>
          <Typography className={classes.title} variant="h3" noWrap>
            Practice Review System
          </Typography>
          <Typography className={classes.versionIndicator} variant="subtitle2">
            v{appConfig.version}
          </Typography>
        </div>
        <div className={classes.tabs}>
          <div className={cx(classes.tab, { selected: tabIndex === Tabs.Main })} onClick={() => setTabIndex(Tabs.Main)}>
            Main
          </div>
          <TopLevelTabHeader tabIndex={Tabs.AdminTasks} displayText="Administrative Tasks" items={adminRoutes} />
          {userHasPermission(Permissions.ReportsView) && (
            <Button className={cx(classes.tab, classes.reportMenuButton)} onClick={() => setReportsMenuOpen(true)} ref={reportsMenuButton}>
              Reports
              <Box sx={{ ml: 1 }}>
                <FontAwesomeIcon icon={faCaretDown} />
              </Box>
            </Button>
          )}
          {appConfig.isDevTestResetEnabled && (
            <div
              className={classes.tab}
              onClick={() => {
                setResetDataDialogIsVisible(true);
              }}>
              Reset Test Data
            </div>
          )}
        </div>
        <div className={classes.grow} />
        <div className={classes.goToPrField}>
          <GoToPrField />
        </div>
        {!userLoading && (
          <div>
            <IconButton onClick={(e) => setUserMenuAnchorElement(e.currentTarget)} size="large">
              <Tooltip title={user.name}>
                <Avatar className={classes.initialsAvatar}>
                  {user.firstName && user.lastName ? `${user.firstName.slice(0, 1)}${user.lastName.slice(0, 1)}` : user.name.slice(0, 1)}
                </Avatar>
              </Tooltip>
            </IconButton>
            <Menu
              anchorEl={userMenuAnchorElement}
              keepMounted
              open={!!userMenuAnchorElement}
              onClose={() => setUserMenuAnchorElement(null)}>
              <MenuItem onClick={() => msalInstance.logoutRedirect()}>Sign out</MenuItem>
            </Menu>
          </div>
        )}
      </Toolbar>
      {tabIndex === Tabs.Main && (
        <Stack direction="row" spacing={1} alignItems="center" className={classes.tabPanel} flexWrap="nowrap">
          <SiteNavItem title="INAs" path={AppRoute.INAs} className={classes.mainButton} />
          {userHasPermission(Permissions.InaViewOthers) && (
            <Route exact path={AppRoute.INAs}>
              <Autocomplete
                className={classes.viewOtherUsersInasSelector}
                options={usersWhoseInasCanBeViewed}
                getOptionLabel={(user: User | null) => (user === null ? "" : user.name)}
                renderInput={(params) => <TextField {...params} placeholder="View Other User's INAs" margin="none" />}
                value={inasUser}
                onChange={(e, newUser) => {
                  setInasUser(newUser);
                }}
              />
            </Route>
          )}
          <SiteNavItem title="Advanced PR Search" path={AppRoute.AdvancedPrSearch} className={classes.mainButton} />
          <SiteNavItem title="My Activity Log" path={AppRoute.MyActivityLog} className={classes.mainButton} />
        </Stack>
      )}

      <TopLevelTabBody tab={Tabs.AdminTasks} currentTab={tabIndex} userHasPermission={userHasPermission} items={adminRoutes} />

      <ResetTestDataDialog isOpen={resetDataDialogIsVisible} setIsOpen={setResetDataDialogIsVisible} />
      <NavSubMenu anchor={reportsMenuButton} open={reportsMenuOpen} onClose={() => setReportsMenuOpen(false)}>
        {_.orderBy(customReports, (r) => r.reportName).map((r) => {
          const normalizedUrl =
            r.url.toLocaleLowerCase().startsWith("https://") || r.url.toLocaleLowerCase().startsWith("http://")
              ? r.url
              : `https://${r.url}`;
          return (
            <MenuItem key={r.id} className={classes.navButton} onClick={() => window.open(normalizedUrl)}>
              {r.reportName}
            </MenuItem>
          );
        })}
      </NavSubMenu>
    </AppBar>
  );
};

export default SiteAppBar;
